import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, Mail, Phone, MapPin, Clock, Eye, Heart, Save, Send } from "lucide-react";
import { api } from "../lib/api";
import { fmtMoney } from "../config";

function fmtSeconds(s) {
  s = Math.max(0, Math.round(s || 0));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const r = s % 60;
  if (m < 60) return r ? `${m}m ${r}s` : `${m}m`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
}

export default function CustomerDetail({ lookupKey, onBack }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(`/dealer/customer/${encodeURIComponent(lookupKey)}`);
        if (alive) setData(data);
      } catch (e) {
        if (alive) setErr(e?.response?.data?.detail || "Couldn't load customer.");
      }
    })();
    return () => { alive = false; };
  }, [lookupKey]);

  if (err) return <div className="text-red-300 p-6" data-testid="cust-detail-err">{err}</div>;
  if (!data) return <div className="text-white/70 p-6" data-testid="cust-detail-loading">Loading customer…</div>;

  const { user, deals, favorites, leads, vehicle_views } = data;
  const totalDwell = vehicle_views.reduce((acc, v) => acc + (v.total_seconds || 0), 0);

  return (
    <div data-testid="cust-detail">
      <button onClick={onBack} className="inline-flex items-center gap-1 text-sm text-white/70 hover:text-white mb-4" data-testid="cust-detail-back">
        <ChevronLeft size={16} /> Back to customers
      </button>

      <div className="bg-white rounded-2xl overflow-hidden">
        {/* Header */}
        <div className="bg-[var(--ssc-navy)] text-white p-5">
          <div className="text-[10px] font-bold uppercase tracking-widest text-[var(--ssc-silver)]">Customer 360</div>
          <div className="text-2xl font-black mt-0.5">{user?.name || lookupKey}</div>
          <div className="text-sm text-[var(--ssc-silver)] flex flex-wrap gap-3 mt-2">
            {user?.email && <a className="text-white inline-flex items-center gap-1" href={`mailto:${user.email}`}><Mail size={14} />{user.email}</a>}
            {user?.phone && <a className="text-white inline-flex items-center gap-1" href={`tel:${user.phone}`}><Phone size={14} />{user.phone}</a>}
            {user?.address && <span className="inline-flex items-center gap-1"><MapPin size={14} />{user.address}</span>}
            {user?.created_at && <span className="inline-flex items-center gap-1"><Clock size={14} />Joined {new Date(user.created_at).toLocaleDateString()}</span>}
          </div>
        </div>

        {/* Snapshot stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 p-5 border-b border-[var(--ssc-line)]">
          <Stat label="Vehicles viewed" value={vehicle_views.length} />
          <Stat label="Total dwell time" value={fmtSeconds(totalDwell)} accent />
          <Stat label="Saved deals" value={deals.length} />
          <Stat label="Lead submissions" value={leads.length} />
        </div>

        {/* Sections */}
        <div className="p-5 space-y-6">
          <Section title="Cars they looked at" icon={<Eye size={16} />} testid="cust-views">
            {vehicle_views.length === 0 ? (
              <Empty text="No vehicle views recorded yet." />
            ) : (
              <ul className="divide-y divide-[var(--ssc-line)]">
                {vehicle_views.map((v) => (
                  <li key={v.slug} className="py-2.5 flex items-center gap-3 text-sm" data-testid={`cust-view-${v.slug}`}>
                    <div className="flex-1 min-w-0">
                      <Link to={`/vehicle/${v.slug}`} className="font-bold text-[var(--ssc-navy)] hover:text-[var(--ssc-blue)] truncate block">
                        {v.title || v.slug}
                      </Link>
                      <div className="text-xs text-[var(--ssc-ink-2)]">
                        {v.view_count} session{v.view_count > 1 ? "s" : ""} · last viewed {new Date(v.last_at).toLocaleString()}
                      </div>
                    </div>
                    <div className="font-extrabold text-[var(--ssc-blue)] tabular-nums">{fmtSeconds(v.total_seconds)}</div>
                  </li>
                ))}
              </ul>
            )}
          </Section>

          <Section title="Saved deals" icon={<Save size={16} />} testid="cust-deals">
            {deals.length === 0 ? <Empty text="No saved deals yet." /> : (
              <ul className="grid sm:grid-cols-2 gap-3">
                {deals.map((d) => (
                  <li key={d.id} className="border border-[var(--ssc-line)] rounded-xl p-3" data-testid={`cust-deal-${d.code}`}>
                    <div className="flex justify-between items-start gap-2">
                      <span className="text-[10px] font-extrabold uppercase tracking-widest text-[var(--ssc-blue)]">{d.deal_type}</span>
                      <span className="font-mono text-xs font-bold bg-[var(--ssc-navy)] text-white px-2 py-0.5 rounded">{d.code}</span>
                    </div>
                    <div className="text-sm font-bold text-[var(--ssc-ink)] mt-1">{d.title}</div>
                    {d.estimate?.dealer_trade_high && (
                      <div className="text-xs text-[var(--ssc-blue)] font-bold mt-1">
                        Trade {fmtMoney(d.estimate.dealer_trade_low)} – {fmtMoney(d.estimate.dealer_trade_high)}
                      </div>
                    )}
                    {d.payment?.monthly && (
                      <div className="text-xs text-[var(--ssc-blue)] font-bold mt-1">
                        {fmtMoney(d.payment.monthly)}/mo for {d.payment.term} mo
                      </div>
                    )}
                    <div className="text-[11px] text-[var(--ssc-ink-2)] mt-1">{new Date(d.created_at).toLocaleString()}</div>
                  </li>
                ))}
              </ul>
            )}
          </Section>

          <Section title="Favorites" icon={<Heart size={16} />} testid="cust-favorites">
            {favorites.length === 0 ? <Empty text="No favorites yet." /> : (
              <ul className="divide-y divide-[var(--ssc-line)]">
                {favorites.map((f) => (
                  <li key={f.id} className="py-2 text-sm" data-testid={`cust-fav-${f.vehicle_slug}`}>
                    <Link to={`/vehicle/${f.vehicle_slug}`} className="font-semibold text-[var(--ssc-navy)] hover:text-[var(--ssc-blue)]">
                      {f.vehicle_title || f.vehicle_slug}
                    </Link>
                    <span className="text-xs text-[var(--ssc-ink-2)] ml-2">{new Date(f.created_at).toLocaleDateString()}</span>
                  </li>
                ))}
              </ul>
            )}
          </Section>

          <Section title="Lead submissions" icon={<Send size={16} />} testid="cust-leads">
            {leads.length === 0 ? <Empty text="No lead form submissions." /> : (
              <ul className="divide-y divide-[var(--ssc-line)]">
                {leads.map((l) => (
                  <li key={l.id} className="py-2.5 text-sm" data-testid={`cust-lead-${l.id}`}>
                    <div className="flex justify-between items-start gap-2">
                      <div className="flex-1">
                        <div className="font-bold text-[var(--ssc-navy)]">{l.form_name || l.lead_type || "Lead"}</div>
                        {l.vehicle_title && <div className="text-xs text-[var(--ssc-ink-2)]">{l.vehicle_title}</div>}
                        {l.message && <div className="text-xs text-[var(--ssc-ink)] mt-1 line-clamp-2">"{l.message}"</div>}
                      </div>
                      <span className={`text-[10px] font-extrabold uppercase tracking-widest px-2 py-0.5 rounded-full border ${l.contacted ? "bg-emerald-50 border-emerald-200 text-emerald-700" : "bg-amber-50 border-amber-200 text-amber-700"}`}>
                        {l.contacted ? "Contacted" : "Open"}
                      </span>
                    </div>
                    <div className="text-[11px] text-[var(--ssc-ink-2)] mt-1">{new Date(l.created_at).toLocaleString()} · {l.lead_source || "?"}</div>
                  </li>
                ))}
              </ul>
            )}
          </Section>
        </div>
      </div>
    </div>
  );
}

const Stat = ({ label, value, accent }) => (
  <div className={`rounded-xl p-3 ${accent ? "bg-[var(--ssc-blue)]/10" : "bg-[var(--ssc-paper)]"}`}>
    <div className="text-[10px] font-bold uppercase tracking-widest text-[var(--ssc-ink-2)]">{label}</div>
    <div className={`text-xl font-black mt-0.5 ${accent ? "text-[var(--ssc-blue)]" : "text-[var(--ssc-navy)]"}`}>{value}</div>
  </div>
);

const Section = ({ title, icon, children, testid }) => (
  <div data-testid={testid}>
    <div className="flex items-center gap-2 mb-2">
      <span className="text-[var(--ssc-blue)]">{icon}</span>
      <h3 className="text-sm font-extrabold uppercase tracking-widest text-[var(--ssc-navy)]">{title}</h3>
    </div>
    {children}
  </div>
);

const Empty = ({ text }) => <div className="text-sm text-[var(--ssc-ink-2)] italic py-1">{text}</div>;
